import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Save, Check, Loader, AlertTriangle, Pencil, Lock, RotateCcw, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { getPremissasFaturamento, putPremissasFaturamento, getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { KpiRow } from "../../components/dashboard/KpiCard";
import { formatCurrency } from "../../utils/orcamento";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária).
const palette = (isLight) => {
  const brand = "#7C3AED";
  return {
    bg: isLight ? "#F8F7FC" : "#0F0A1E",
    card: isLight ? "#FFFFFF" : "#1A1030",
    border: isLight ? "#E5E0F0" : "#2E2050",
    text: isLight ? "#1A1030" : "#EDE9FA",
    muted: isLight ? "#7C6FA0" : "#9585C0",
    brand,
    success: "#10B981",
    danger: "#EF4444",
    inputBg: isLight ? "#F7F6FB" : "#241A3D",
    staticBg: isLight ? "#F3F1FA" : "#1F1638",
    zebra: isLight ? "#FAF9FD" : "#170F29",
    totalBg: isLight ? `${brand}12` : `${brand}26`,
  };
};

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

const formatPercent = (v) =>
  `${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const parseNum = (s) => {
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
};

// ── Variação ano a ano usada no card "Vendas & Meta — Ano Anterior": "moeda" mostra %, "percentual" mostra p.p. ──
function calcularVariacao(atual, anterior, tipo) {
  if (anterior === null || anterior === undefined) return null;
  const diff = atual - anterior;
  if (tipo === "percentual") {
    return { sentido: diff > 0 ? "up" : diff < 0 ? "down" : "flat", texto: `${diff >= 0 ? "+" : ""}${diff.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} p.p.` };
  }
  if (anterior === 0) {
    if (diff === 0) return { sentido: "flat", texto: "0,00%" };
    return { sentido: diff > 0 ? "up" : "down", texto: "Novo" };
  }
  const pct = (diff / Math.abs(anterior)) * 100;
  return { sentido: diff > 0 ? "up" : diff < 0 ? "down" : "flat", texto: `${pct >= 0 ? "+" : ""}${pct.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` };
}

const FORM_VAZIO = {
  vendas_totais: "0",
  pipeline_bruto: "0",
  meta_faturamento_bruto: "0",
  faturamento_backlog_bruto: "0",
  aliquota_liquida_backlog_pct: "100",
  aliquota_liquida_novas_vendas_pct: "100",
  pct_mb_backlog: "0",
  pct_mb_novas_vendas: "0",
};

export default function PremissasFaturamento() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [versao, setVersao] = useState("Principal");
  const [form, setForm] = useState(FORM_VAZIO);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState(false);

  // ── Vendas & Meta do Ano Anterior: mostrado lado a lado na própria tela
  // principal, para comparação direta sem precisar trocar de aba, e também
  // editável (Pipeline, Vendas Totais e Meta) para permitir correções sem
  // precisar trocar o seletor de ano. ──
  const [formAnoAnterior, setFormAnoAnterior] = useState(FORM_VAZIO);
  const [anoAnteriorLoading, setAnoAnteriorLoading] = useState(true);

  useEffect(() => {
    async function carregar() {
      setLoading(true);
      setErro("");
      setSucesso(false);
      try {
        const dados = await getPremissasFaturamento(ano, versao);
        setForm({
          vendas_totais: String(dados.vendas_totais ?? 0),
          pipeline_bruto: String(dados.pipeline_bruto ?? 0),
          meta_faturamento_bruto: String(dados.meta_faturamento_bruto ?? 0),
          faturamento_backlog_bruto: String(dados.faturamento_backlog_bruto ?? 0),
          aliquota_liquida_backlog_pct: String(dados.aliquota_liquida_backlog_pct ?? 100),
          aliquota_liquida_novas_vendas_pct: String(dados.aliquota_liquida_novas_vendas_pct ?? 100),
          pct_mb_backlog: String(dados.pct_mb_backlog ?? 0),
          pct_mb_novas_vendas: String(dados.pct_mb_novas_vendas ?? 0),
        });
      } catch (e) {
        setErro(e.message);
      } finally {
        setLoading(false);
      }
    }
    carregar();
  }, [ano, versao]);

  // ── Vendas & Meta do Ano Anterior: busca em paralelo ao ano corrente,
  // reaproveitando o mesmo endpoint de 1 ano por vez. ──
  useEffect(() => {
    let cancelado = false;
    async function carregarAnoAnterior() {
      setAnoAnteriorLoading(true);
      try {
        const dados = await getPremissasFaturamento(ano - 1, versao);
        if (!cancelado) {
          setFormAnoAnterior({
            vendas_totais: String(dados.vendas_totais ?? 0),
            pipeline_bruto: String(dados.pipeline_bruto ?? 0),
            meta_faturamento_bruto: String(dados.meta_faturamento_bruto ?? 0),
            faturamento_backlog_bruto: String(dados.faturamento_backlog_bruto ?? 0),
            aliquota_liquida_backlog_pct: String(dados.aliquota_liquida_backlog_pct ?? 100),
            aliquota_liquida_novas_vendas_pct: String(dados.aliquota_liquida_novas_vendas_pct ?? 100),
            pct_mb_backlog: String(dados.pct_mb_backlog ?? 0),
            pct_mb_novas_vendas: String(dados.pct_mb_novas_vendas ?? 0),
          });
        }
      } catch (e) {
        if (!cancelado) setFormAnoAnterior(FORM_VAZIO);
      } finally {
        if (!cancelado) setAnoAnteriorLoading(false);
      }
    }
    carregarAnoAnterior();
    return () => {
      cancelado = true;
    };
  }, [ano, versao]);

  function setCampo(campo, valor) {
    setForm((f) => ({ ...f, [campo]: valor }));
    setSucesso(false);
  }

  function setCampoAnoAnterior(campo, valor) {
    setFormAnoAnterior((f) => ({ ...f, [campo]: valor }));
    setSucesso(false);
  }

  // ── Edição flexível: digitar o % recalcula o Faturamento Backlog com base na Meta atual ──
  // (Meta Faturamento é editável; Novas Vendas é sempre o restante = Meta - Backlog.)
  function setBacklogPorPercentualDaMeta(pct) {
    const novoValor = (pct / 100) * metaFaturamentoBruto;
    setCampo("faturamento_backlog_bruto", String(Math.round(novoValor * 100) / 100));
  }

  function restaurarPadrao() {
    if (window.confirm("Restaurar todos os campos para os valores padrão? Alterações não salvas serão perdidas.")) {
      setForm(FORM_VAZIO);
      setSucesso(false);
    }
  }

  async function salvar() {
    setSalvando(true);
    setErro("");
    setSucesso(false);
    try {
      await Promise.all([
        putPremissasFaturamento({
          ano_exercicio: ano,
          versao_orcamento: versao,
          vendas_totais: parseNum(form.vendas_totais),
          pipeline_bruto: parseNum(form.pipeline_bruto),
          meta_faturamento_bruto: parseNum(form.meta_faturamento_bruto),
          faturamento_backlog_bruto: parseNum(form.faturamento_backlog_bruto),
          aliquota_liquida_backlog_pct: parseNum(form.aliquota_liquida_backlog_pct),
          aliquota_liquida_novas_vendas_pct: parseNum(form.aliquota_liquida_novas_vendas_pct),
          pct_mb_backlog: parseNum(form.pct_mb_backlog),
          pct_mb_novas_vendas: parseNum(form.pct_mb_novas_vendas),
        }),
        putPremissasFaturamento({
          ano_exercicio: ano - 1,
          versao_orcamento: versao,
          vendas_totais: parseNum(formAnoAnterior.vendas_totais),
          pipeline_bruto: parseNum(formAnoAnterior.pipeline_bruto),
          meta_faturamento_bruto: parseNum(formAnoAnterior.meta_faturamento_bruto),
          faturamento_backlog_bruto: parseNum(formAnoAnterior.faturamento_backlog_bruto),
          aliquota_liquida_backlog_pct: parseNum(formAnoAnterior.aliquota_liquida_backlog_pct),
          aliquota_liquida_novas_vendas_pct: parseNum(formAnoAnterior.aliquota_liquida_novas_vendas_pct),
          pct_mb_backlog: parseNum(formAnoAnterior.pct_mb_backlog),
          pct_mb_novas_vendas: parseNum(formAnoAnterior.pct_mb_novas_vendas),
        }),
      ]);
      setSucesso(true);
      setTimeout(() => setSucesso(false), 3000);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  // ── Cálculos dinâmicos (reatividade em tempo real) ──────────────────────────
  // Meta Faturamento é editável; Novas Vendas é sempre o restante (Meta - Backlog).
  const vendasTotais = parseNum(form.vendas_totais);
  const pipelineBruto = parseNum(form.pipeline_bruto);
  // % de quanto do Pipeline já virou Vendas — ex: Pipeline 100 e Vendas 20 ⇒ Vendas é 20% do
  // Pipeline (Vendas é o numerador, Pipeline é a base/denominador, não o contrário).
  const pctVendasSobrePipeline = pipelineBruto > 0 ? (vendasTotais / pipelineBruto) * 100 : 0;
  const metaFaturamentoBruto = parseNum(form.meta_faturamento_bruto);
  const backlogBruto = parseNum(form.faturamento_backlog_bruto);
  const novasVendasBruto = metaFaturamentoBruto - backlogBruto;
  const aliquotaBacklog = parseNum(form.aliquota_liquida_backlog_pct);
  const aliquotaNovasVendas = parseNum(form.aliquota_liquida_novas_vendas_pct);
  const pctMbBacklog = parseNum(form.pct_mb_backlog);
  const pctMbNovasVendas = parseNum(form.pct_mb_novas_vendas);

  const pctDaMetaBacklog = metaFaturamentoBruto > 0 ? (backlogBruto / metaFaturamentoBruto) * 100 : 0;
  const pctDaMetaNovasVendas = metaFaturamentoBruto > 0 ? (novasVendasBruto / metaFaturamentoBruto) * 100 : 0;
  const pctSobreVendas = vendasTotais > 0 ? (metaFaturamentoBruto / vendasTotais) * 100 : 0;

  const backlogLiquido = backlogBruto * (aliquotaBacklog / 100);
  const novasVendasLiquido = novasVendasBruto * (aliquotaNovasVendas / 100);
  const metaFaturamentoLiquido = backlogLiquido + novasVendasLiquido;

  const mbBacklog = backlogLiquido * (pctMbBacklog / 100);
  const mbNovasVendas = novasVendasLiquido * (pctMbNovasVendas / 100);
  const mbTotal = mbBacklog + mbNovasVendas;
  const pctMbTotal = metaFaturamentoLiquido > 0 ? (mbTotal / metaFaturamentoLiquido) * 100 : 0;

  // ── Ano Anterior: mesmos cálculos reativos do ano atual, a partir do próprio
  // formAnoAnterior — os campos brutos (Pipeline, Vendas, Meta) são editáveis; os
  // percentuais aqui são sempre calculados a partir deles. ──
  const vendasTotaisAnoAnterior = parseNum(formAnoAnterior.vendas_totais);
  const pipelineBrutoAnoAnterior = parseNum(formAnoAnterior.pipeline_bruto);
  const pctVendasSobrePipelineAnoAnterior = pipelineBrutoAnoAnterior > 0 ? (vendasTotaisAnoAnterior / pipelineBrutoAnoAnterior) * 100 : 0;
  const metaFaturamentoBrutoAnoAnterior = parseNum(formAnoAnterior.meta_faturamento_bruto);
  const pctSobreVendasAnoAnterior = vendasTotaisAnoAnterior > 0 ? (metaFaturamentoBrutoAnoAnterior / vendasTotaisAnoAnterior) * 100 : 0;

  const kpiItems = [
    { label: "Vendas Totais", value: formatCurrency(vendasTotais), color: C.text },
    { label: "Meta Faturamento Líquido", value: formatCurrency(metaFaturamentoLiquido), color: C.brand },
    { label: "Margem Bruta Total", value: formatCurrency(mbTotal), color: C.success },
    { label: "% MB Total", value: formatPercent(pctMbTotal), color: C.success },
  ];

  const resumoRows = [
    { label: "Faturamento Backlog", pctMeta: pctDaMetaBacklog, bruto: backlogBruto, aliquota: aliquotaBacklog, liquido: backlogLiquido, pctMb: pctMbBacklog, mb: mbBacklog },
    { label: "Novas Vendas", pctMeta: pctDaMetaNovasVendas, bruto: novasVendasBruto, aliquota: aliquotaNovasVendas, liquido: novasVendasLiquido, pctMb: pctMbNovasVendas, mb: mbNovasVendas },
  ];
  const resumoTotal = { bruto: metaFaturamentoBruto, liquido: metaFaturamentoLiquido, pctMb: pctMbTotal, mb: mbTotal };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Header */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Premissas de Faturamento</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="text" value={versao} onChange={(e) => setVersao(e.target.value)}
              placeholder="Versão do orçamento"
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600, width: 150 }}
            />
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 28 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          {/* Barra de ações */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>Premissas de Faturamento</h1>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>
                {`Exercício ${ano} · Versão "${versao}" — visão executiva de vendas, faturamento e margem bruta projetados.`}
                {!isAdmin && " Somente admin edita — os demais perfis visualizam."}
              </p>
            </div>
            {isAdmin && (
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button
                  onClick={restaurarPadrao}
                  disabled={salvando || loading}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "transparent", border: `1px solid ${C.border}`, borderRadius: 10,
                    padding: "11px 18px", color: C.text, fontSize: 13, fontWeight: 700,
                    cursor: salvando || loading ? "not-allowed" : "pointer",
                    opacity: salvando || loading ? 0.6 : 1, fontFamily: "inherit",
                  }}
                >
                  <RotateCcw size={15} /> Restaurar Padrão
                </button>
                <button
                  onClick={salvar}
                  disabled={salvando || loading}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: sucesso ? C.success : C.brand, border: "none", borderRadius: 10,
                    padding: "11px 20px", color: "#fff", fontSize: 14, fontWeight: 700,
                    cursor: salvando || loading ? "not-allowed" : "pointer",
                    opacity: salvando || loading ? 0.7 : 1, fontFamily: "inherit",
                    transition: "background 0.2s",
                  }}
                >
                  {salvando ? <Loader size={16} className="spin-icon" /> : sucesso ? <Check size={16} /> : <Save size={16} />}
                  {salvando ? "Salvando..." : sucesso ? "Salvo!" : "Salvar Premissas"}
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
          ) : (
            <>
              {/* 1. Header e KPIs Principais */}
              <section>
                <SectionHeading C={C} title="Indicadores Principais" subtitle="Resultado consolidado a partir das premissas abaixo." />
                <KpiRow items={kpiItems} C={C} />
              </section>

              {/* 2. Bloco de Entrada de Premissas */}
              <section>
                <SectionHeading
                  C={C}
                  title="Premissas de Entrada"
                  subtitle="Preencha backlog, novas vendas e impostos/margens — os totais recalculam automaticamente."
                />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                  <Card C={C} title={`Vendas & Meta — ${ano - 1}`}>
                    {anoAnteriorLoading ? (
                      <div style={{ padding: "12px 0", textAlign: "center", color: C.muted, fontSize: 12 }}>Carregando…</div>
                    ) : (
                      <>
                        <Field C={C} label="Pipeline" kind="editavel" hint="Faturamento bruto potencial em prospecção — informativo, não entra na Meta.">
                          <CurrencyInput C={C} value={pipelineBrutoAnoAnterior} onChange={(v) => setCampoAnoAnterior("pipeline_bruto", String(v))} disabled={!isAdmin} />
                        </Field>
                        <Field C={C} label="Vendas Totais" kind="editavel">
                          <CurrencyInput C={C} value={vendasTotaisAnoAnterior} onChange={(v) => setCampoAnoAnterior("vendas_totais", String(v))} disabled={!isAdmin} />
                        </Field>
                        <Field C={C} label="% Vendas sobre Pipeline" kind="calculado">
                          <StaticValue C={C}>{formatPercent(pctVendasSobrePipelineAnoAnterior)}</StaticValue>
                        </Field>
                        <Field C={C} label="Meta de Faturamento Bruto" kind="editavel">
                          <CurrencyInput C={C} value={metaFaturamentoBrutoAnoAnterior} onChange={(v) => setCampoAnoAnterior("meta_faturamento_bruto", String(v))} strong disabled={!isAdmin} />
                        </Field>
                        <Field C={C} label="% sobre as Vendas" kind="calculado">
                          <StaticValue C={C}>{formatPercent(pctSobreVendasAnoAnterior)}</StaticValue>
                        </Field>
                      </>
                    )}
                  </Card>

                  <Card C={C} title="Vendas & Meta">
                    <Field C={C} label="Pipeline" kind="editavel" hint="Faturamento bruto potencial em prospecção — informativo, não entra na Meta.">
                      <ComVariacao C={C} variacao={calcularVariacao(pipelineBruto, pipelineBrutoAnoAnterior, "moeda")}>
                        <CurrencyInput C={C} value={pipelineBruto} onChange={(v) => setCampo("pipeline_bruto", String(v))} disabled={!isAdmin} />
                      </ComVariacao>
                    </Field>
                    <Field C={C} label="Vendas Totais" kind="editavel">
                      <ComVariacao C={C} variacao={calcularVariacao(vendasTotais, vendasTotaisAnoAnterior, "moeda")}>
                        <CurrencyInput C={C} value={vendasTotais} onChange={(v) => setCampo("vendas_totais", String(v))} disabled={!isAdmin} />
                      </ComVariacao>
                    </Field>
                    <Field C={C} label="% Vendas sobre Pipeline" kind="calculado">
                      <ComVariacao C={C} variacao={calcularVariacao(pctVendasSobrePipeline, pctVendasSobrePipelineAnoAnterior, "percentual")}>
                        <StaticValue C={C}>{formatPercent(pctVendasSobrePipeline)}</StaticValue>
                      </ComVariacao>
                    </Field>
                    <Field C={C} label="Meta de Faturamento Bruto" kind="editavel">
                      <ComVariacao C={C} variacao={calcularVariacao(metaFaturamentoBruto, metaFaturamentoBrutoAnoAnterior, "moeda")}>
                        <CurrencyInput C={C} value={metaFaturamentoBruto} onChange={(v) => setCampo("meta_faturamento_bruto", String(v))} strong disabled={!isAdmin} />
                      </ComVariacao>
                    </Field>
                    <Field C={C} label="% sobre as Vendas" kind="calculado">
                      <ComVariacao C={C} variacao={calcularVariacao(pctSobreVendas, pctSobreVendasAnoAnterior, "percentual")}>
                        <StaticValue C={C}>{formatPercent(pctSobreVendas)}</StaticValue>
                      </ComVariacao>
                    </Field>
                  </Card>

                  <Card C={C} title="Backlog">
                    <Field C={C} label="Faturamento Backlog (Bruto)" kind="editavel">
                      <CurrencyInput C={C} value={backlogBruto} onChange={(v) => setCampo("faturamento_backlog_bruto", String(v))} disabled={!isAdmin} />
                    </Field>
                    <Field C={C} label="% da Meta (Backlog)" kind="editavel" hint="Editar recalcula o valor de backlog com base na Meta.">
                      <PercentInput C={C} value={pctDaMetaBacklog} onChange={setBacklogPorPercentualDaMeta} disabled={!isAdmin} />
                    </Field>
                  </Card>

                  <Card C={C} title="Novas Vendas">
                    <Field C={C} label="Faturamento Novas Vendas (Bruto)" kind="calculado" hint="Meta de Faturamento − Faturamento Backlog.">
                      <StaticValue C={C}>{formatCurrency(novasVendasBruto)}</StaticValue>
                    </Field>
                    <Field C={C} label="% da Meta (Novas Vendas)" kind="calculado">
                      <StaticValue C={C}>{formatPercent(pctDaMetaNovasVendas)}</StaticValue>
                    </Field>
                  </Card>

                  <Card C={C} title="Impostos & Margens">
                    <Field C={C} label="Alíquota Líquida — Backlog" kind="editavel">
                      <PercentInput C={C} value={aliquotaBacklog} onChange={(v) => setCampo("aliquota_liquida_backlog_pct", String(v))} disabled={!isAdmin} />
                    </Field>
                    <Field C={C} label="Alíquota Líquida — Novas Vendas" kind="editavel">
                      <PercentInput C={C} value={aliquotaNovasVendas} onChange={(v) => setCampo("aliquota_liquida_novas_vendas_pct", String(v))} disabled={!isAdmin} />
                    </Field>
                    <Field C={C} label="% Margem Bruta — Backlog" kind="editavel">
                      <PercentInput C={C} value={pctMbBacklog} onChange={(v) => setCampo("pct_mb_backlog", String(v))} disabled={!isAdmin} />
                    </Field>
                    <Field C={C} label="% Margem Bruta — Novas Vendas" kind="editavel">
                      <PercentInput C={C} value={pctMbNovasVendas} onChange={(v) => setCampo("pct_mb_novas_vendas", String(v))} disabled={!isAdmin} />
                    </Field>
                  </Card>
                </div>
              </section>

              {/* 3. Resumo Visual de DRE/Margem */}
              <section>
                <SectionHeading
                  C={C}
                  title="Resumo de DRE & Margem"
                  subtitle="Visão consolidada de faturamento bruto, líquido e margem bruta projetada."
                />
                <ResumoDreTable C={C} rows={resumoRows} total={resumoTotal} />
              </section>
            </>
          )}
        </main>
      </div>

      <style>{`
        @keyframes spin-premissas { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin-icon { animation: spin-premissas 0.8s linear infinite; }
      `}</style>
    </div>
  );
}

// ── Componentes locais ───────────────────────────────────────────────────────

function SectionHeading({ C, title, subtitle }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: C.text }}>{title}</h2>
      {subtitle && <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{subtitle}</p>}
    </div>
  );
}

function Card({ C, title, children }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
      padding: 18, display: "flex", flexDirection: "column", gap: 14,
    }}>
      <h3 style={{ margin: 0, fontSize: 12, fontWeight: 800, color: C.text, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

const BADGE_CONFIG = {
  editavel: { label: "Editável", Icone: Pencil },
  historico: { label: "Ano Anterior", Icone: Lock },
  calculado: { label: "Calculado", Icone: Lock },
};

function Badge({ C, kind }) {
  const { label, Icone } = BADGE_CONFIG[kind] || BADGE_CONFIG.calculado;
  const tone = kind === "editavel" ? C.brand : C.muted;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
      padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap",
      color: tone, background: `${tone}18`, border: `1px solid ${tone}40`,
    }}>
      <Icone size={10} />
      {label}
    </span>
  );
}

function Field({ C, label, kind, children, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>{label}</span>
        <Badge C={C} kind={kind} />
      </div>
      {children}
      {hint && <span style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{hint}</span>}
    </div>
  );
}

function StaticValue({ C, children }) {
  return (
    <div style={{
      width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10,
      background: C.staticBg, border: `1px solid ${C.border}`,
      fontSize: 14, fontWeight: 700, color: C.text, textAlign: "right",
    }}>
      {children}
    </div>
  );
}

// Envolve um valor do ano corrente (editável ou calculado) com o badge de
// variação em relação ao ano anterior, exibido logo abaixo, alinhado à direita.
function ComVariacao({ C, variacao, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {children}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <VariacaoBadge C={C} variacao={variacao} />
      </div>
    </div>
  );
}

function inputBoxStyle(C, focused, strong) {
  return {
    width: "100%", boxSizing: "border-box",
    background: C.inputBg, border: `1px solid ${focused ? C.brand : C.border}`,
    borderRadius: 10, padding: "10px 12px",
    fontSize: strong ? 15 : 14, fontWeight: strong ? 800 : 600, color: C.text,
    textAlign: "right", outline: "none", fontFamily: "inherit",
    boxShadow: focused ? `0 0 0 3px ${C.brand}26` : "none",
    transition: "box-shadow 0.15s, border-color 0.15s",
  };
}

// Máscara de moeda: dígitos são interpretados como centavos, formatando em
// "R$ 0.000,00" a cada tecla digitada e mantendo o formato ao desfocar.
function CurrencyInput({ C, value, onChange, strong, disabled }) {
  const [focused, setFocused] = useState(false);
  const [digits, setDigits] = useState(() => Math.round(Number(value || 0) * 100));
  const lastRef = useRef(digits);

  useEffect(() => {
    const externo = Math.round(Number(value || 0) * 100);
    if (externo !== lastRef.current) {
      setDigits(externo);
      lastRef.current = externo;
    }
  }, [value]);

  function handleChange(e) {
    const raw = e.target.value.replace(/\D/g, "");
    const next = raw === "" ? 0 : parseInt(raw, 10);
    setDigits(next);
    lastRef.current = next;
    onChange(next / 100);
  }

  return (
    <input
      inputMode="numeric"
      value={formatCurrency(digits / 100)}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      disabled={disabled}
      style={inputBoxStyle(C, focused, strong)}
    />
  );
}

// Máscara de porcentagem: dígitos são interpretados como centésimos de ponto
// percentual (2 casas decimais), formatando como "57,10%" a cada tecla.
function PercentInput({ C, value, onChange, strong, disabled }) {
  const [focused, setFocused] = useState(false);
  const [digits, setDigits] = useState(() => Math.round(Number(value || 0) * 100));
  const lastRef = useRef(digits);

  useEffect(() => {
    const externo = Math.round(Number(value || 0) * 100);
    if (externo !== lastRef.current) {
      setDigits(externo);
      lastRef.current = externo;
    }
  }, [value]);

  function handleChange(e) {
    const raw = e.target.value.replace(/\D/g, "");
    const next = raw === "" ? 0 : parseInt(raw, 10);
    setDigits(next);
    lastRef.current = next;
    onChange(next / 100);
  }

  return (
    <input
      inputMode="numeric"
      value={formatPercent(digits / 100)}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      disabled={disabled}
      style={inputBoxStyle(C, focused, strong)}
    />
  );
}

const RESUMO_COLUNAS = ["Descrição", "% da Meta", "Faturamento Bruto", "Alíquota", "Faturamento Líquido", "% MB", "Margem Bruta"];

function ResumoDreTable({ C, rows, total }) {
  const cellStyle = { padding: "14px 16px", color: C.text, whiteSpace: "nowrap" };
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 760 }}>
          <thead>
            <tr>
              {RESUMO_COLUNAS.map((h, i) => (
                <th key={h} style={{
                  padding: "12px 16px", fontSize: 11, fontWeight: 700, color: C.muted,
                  textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
                  textAlign: i === 0 ? "left" : "right", borderBottom: `1px solid ${C.border}`,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.label} style={{ background: i % 2 === 1 ? C.zebra : "transparent" }}>
                <td style={{ ...cellStyle, textAlign: "left", fontWeight: 600 }}>{r.label}</td>
                <td style={{ ...cellStyle, textAlign: "right" }}>{formatPercent(r.pctMeta)}</td>
                <td style={{ ...cellStyle, textAlign: "right" }}>{formatCurrency(r.bruto)}</td>
                <td style={{ ...cellStyle, textAlign: "right" }}>{formatPercent(r.aliquota)}</td>
                <td style={{ ...cellStyle, textAlign: "right" }}>{formatCurrency(r.liquido)}</td>
                <td style={{ ...cellStyle, textAlign: "right" }}>{formatPercent(r.pctMb)}</td>
                <td style={{ ...cellStyle, textAlign: "right", fontWeight: 700 }}>{formatCurrency(r.mb)}</td>
              </tr>
            ))}
            <tr style={{ background: C.totalBg }}>
              <td style={{ ...cellStyle, textAlign: "left", fontWeight: 800, borderTop: `2px solid ${C.brand}` }}>Total</td>
              <td style={{ ...cellStyle, borderTop: `2px solid ${C.brand}` }}></td>
              <td style={{ ...cellStyle, textAlign: "right", fontWeight: 800, borderTop: `2px solid ${C.brand}` }}>{formatCurrency(total.bruto)}</td>
              <td style={{ ...cellStyle, borderTop: `2px solid ${C.brand}` }}></td>
              <td style={{ ...cellStyle, textAlign: "right", fontWeight: 800, borderTop: `2px solid ${C.brand}` }}>{formatCurrency(total.liquido)}</td>
              <td style={{ ...cellStyle, textAlign: "right", fontWeight: 800, color: C.brand, borderTop: `2px solid ${C.brand}` }}>{formatPercent(total.pctMb)}</td>
              <td style={{ ...cellStyle, textAlign: "right", fontWeight: 800, color: C.brand, borderTop: `2px solid ${C.brand}` }}>{formatCurrency(total.mb)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VariacaoBadge({ C, variacao }) {
  if (!variacao) return null;
  const cor = variacao.sentido === "up" ? C.success : variacao.sentido === "down" ? C.danger : C.muted;
  const Icone = variacao.sentido === "up" ? TrendingUp : variacao.sentido === "down" ? TrendingDown : Minus;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 700, color: cor, whiteSpace: "nowrap" }}>
      <Icone size={11} />
      {variacao.texto}
    </span>
  );
}

